if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.cartState = null;
        this.cartStateFetchedAt = 0;
        this.cartStatePromise = null;
        this.prefetchCartState();
      }

      getCartSectionIds() {
        return this.cart.getSectionsToRender().map((section) => section.id);
      }

      appendCartSections(formData) {
        if (!this.cart) return;

        formData.append('sections', this.getCartSectionIds());
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      getRequestedQuantity(formData) {
        const quantity = parseInt(formData.get('quantity') || '1', 10);

        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      getPropertyName(fieldName) {
        const match = /^properties\[(.*)\]$/.exec(fieldName);

        return match ? match[1].trim() : null;
      }

      appendCustomilyProperties(formData) {
        const root = document.querySelector('#customily-options');
        if (!root) return;

        const valuesByField = new Map();
        const controls = Array.from(root.querySelectorAll('input, textarea, select'));

        controls.forEach((control) => {
          const fieldName = control.name || '';
          const propertyName = this.getPropertyName(fieldName);
          const type = (control.type || '').toLowerCase();

          if (!propertyName || propertyName.startsWith('_') || control.disabled) return;
          if (['button', 'submit', 'reset', 'hidden'].includes(type)) return;
          if (['radio', 'checkbox'].includes(type) && !control.checked) return;

          let values = [];

          if (control.tagName === 'SELECT' && control.multiple) {
            values = Array.from(control.selectedOptions).map((option) => option.value);
          } else if (type === 'file') {
            values = Array.from(control.files || []).filter((file) => file.size > 0);
          } else {
            values = [control.value];
          }

          values = values.filter((value) => {
            if (typeof value !== 'string') return true;
            return this.normalizePropertyValue(value) !== '';
          });

          if (values.length === 0) return;

          const existingValues = valuesByField.get(fieldName) || [];
          valuesByField.set(fieldName, existingValues.concat(values));
        });

        valuesByField.forEach((values, fieldName) => {
          formData.delete(fieldName);
          values.forEach((value) => formData.append(fieldName, value));
        });
      }

      normalizePropertyValue(value) {
        return String(value ?? '').trim();
      }

      hashString(value) {
        let hash = 2166136261;

        for (let index = 0; index < value.length; index += 1) {
          hash ^= value.charCodeAt(index);
          hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
        }

        return (hash >>> 0).toString(36);
      }

      isPlaceholderPersonalization(name, value) {
        return name.toLowerCase() === 'edit personalization' && value.toLowerCase() === 'enter abc';
      }

      isStableSignatureProperty(name) {
        return name.toLowerCase().replace(/[\s_-]+/g, '') === 'dmpersonalizationsignature';
      }

      hasStablePersonalizationSignature(formData) {
        return this.normalizePropertyValue(formData.get('properties[_dm_personalization_signature]')) !== '';
      }

      isVolatileLineProperty(name, hasStableSignature = false) {
        const normalizedName = name.toLowerCase().replace(/[\s_-]+/g, '');

        return (
          normalizedName.includes('cartitemkey') ||
          normalizedName.includes('lineitemkey') ||
          normalizedName.includes('uniqueid') ||
          normalizedName.includes('uuid') ||
          normalizedName.includes('timestamp') ||
          normalizedName.includes('session') ||
          (hasStableSignature &&
            (normalizedName === 'customilypreview' || normalizedName === 'customilyproductionurl'))
        );
      }

      getComparablePropertyEntries(formData, hasStableSignature = false) {
        const entries = [];

        for (const [fieldName, fieldValue] of formData.entries()) {
          const propertyName = this.getPropertyName(fieldName);
          if (!propertyName || this.isVolatileLineProperty(propertyName, hasStableSignature)) continue;

          const propertyValue = this.normalizePropertyValue(fieldValue);
          if (!propertyValue || this.isPlaceholderPersonalization(propertyName, propertyValue)) continue;
          if (this.isStableSignatureProperty(propertyName)) continue;

          entries.push([propertyName, propertyValue]);
        }

        return entries;
      }

      getComparablePropertiesFromForm(formData) {
        const properties = {};
        const hasStableSignature = this.hasStablePersonalizationSignature(formData);

        this.getComparablePropertyEntries(formData, hasStableSignature).forEach(([propertyName, propertyValue]) => {
          properties[propertyName] = propertyValue;
        });

        const signature = this.normalizePropertyValue(formData.get('properties[_dm_personalization_signature]'));
        if (signature) properties._dm_personalization_signature = signature;

        return properties;
      }

      getComparablePropertiesFromCartItem(item, hasStableSignature = false) {
        const properties = {};

        Object.entries(item.properties || {}).forEach(([propertyName, propertyValue]) => {
          const normalizedName = this.normalizePropertyValue(propertyName);
          if (!normalizedName || this.isVolatileLineProperty(normalizedName, hasStableSignature)) return;

          const normalizedValue = this.normalizePropertyValue(propertyValue);
          if (!normalizedValue || this.isPlaceholderPersonalization(normalizedName, normalizedValue)) return;

          properties[normalizedName] = normalizedValue;
        });

        return properties;
      }

      propertiesMatch(formProperties, itemProperties) {
        const formKeys = Object.keys(formProperties).sort();
        const itemKeys = Object.keys(itemProperties).sort();

        if (formKeys.length !== itemKeys.length) return false;

        return formKeys.every((key, index) => key === itemKeys[index] && formProperties[key] === itemProperties[key]);
      }

      getControlLabel(control, index) {
        return (
          control.name ||
          control.id ||
          control.getAttribute('aria-label') ||
          control.closest('label')?.textContent?.trim() ||
          `control-${index}`
        );
      }

      getCustomilyControlSignatureParts() {
        const root = document.querySelector('#customily-options');
        if (!root) return [];

        const parts = [];
        const controls = Array.from(root.querySelectorAll('input, textarea, select'));

        controls.forEach((control, index) => {
          if (control.disabled) return;

          const type = (control.type || '').toLowerCase();
          if (['button', 'submit', 'reset', 'hidden'].includes(type)) return;
          if (['radio', 'checkbox'].includes(type) && !control.checked) return;

          let value = '';
          if (control.tagName === 'SELECT') {
            value = Array.from(control.selectedOptions)
              .map((option) => `${option.value}:${option.textContent?.trim() || ''}`)
              .join('|');
          } else if (type === 'file') {
            value = Array.from(control.files || [])
              .map((file) => `${file.name}:${file.size}`)
              .join('|');
          } else {
            value = control.value;
          }

          parts.push(`${this.getControlLabel(control, index)}=${this.normalizePropertyValue(value)}`);
        });

        Array.from(root.querySelectorAll('button[aria-pressed="true"], [role="button"][aria-pressed="true"], .selected, .is-selected, .active')).forEach(
          (element, index) => {
            const value = this.normalizePropertyValue(
              element.getAttribute('data-value') ||
                element.getAttribute('aria-label') ||
                element.textContent ||
                element.id ||
                `selected-${index}`
            );

            if (value) parts.push(`selected-${index}=${value}`);
          }
        );

        return parts;
      }

      setStablePersonalizationSignature(formData) {
        const comparableProperties = this.getComparablePropertyEntries(formData, true);
        const customilyParts = this.getCustomilyControlSignatureParts();
        if (comparableProperties.length === 0 && customilyParts.length === 0) return;

        const signaturePayload = JSON.stringify({
          properties: comparableProperties.sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey)),
          customily: customilyParts.sort(),
        });

        formData.set('properties[_dm_personalization_signature]', this.hashString(signaturePayload));
      }

      sellingPlanMatches(formData, item) {
        const formSellingPlan = this.normalizePropertyValue(formData.get('selling_plan'));
        const itemSellingPlan = this.normalizePropertyValue(item.selling_plan_allocation?.selling_plan?.id);

        return formSellingPlan === itemSellingPlan;
      }

      async fetchCartState() {
        const response = await fetch(`${routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) return null;

        const cart = await response.json();
        this.cartState = cart;
        this.cartStateFetchedAt = Date.now();

        return cart;
      }

      prefetchCartState() {
        if (!this.cart || this.cartStatePromise) return this.cartStatePromise;

        this.cartStatePromise = this.fetchCartState()
          .catch(() => null)
          .finally(() => {
            this.cartStatePromise = null;
          });

        return this.cartStatePromise;
      }

      async getCartStateForMerge() {
        if (this.cartState && Date.now() - this.cartStateFetchedAt < 30000) return this.cartState;
        if (!this.cartStatePromise) this.prefetchCartState();

        return Promise.race([
          this.cartStatePromise || Promise.resolve(null),
          new Promise((resolve) => setTimeout(() => resolve(null), 120)),
        ]);
      }

      rememberCartState(cart) {
        if (!cart?.items) return;

        this.cartState = cart;
        this.cartStateFetchedAt = Date.now();
      }

      async findMatchingCartLines(formData) {
        if (!this.cart) return [];

        const variantId = parseInt(formData.get('id'), 10);
        if (!variantId) return [];

        try {
          const cart = await this.getCartStateForMerge();
          if (!cart) return [];

          const formProperties = this.getComparablePropertiesFromForm(formData);
          const hasStableSignature = this.hasStablePersonalizationSignature(formData);

          return (cart.items || []).filter((item) => {
            return (
              item.variant_id === variantId &&
              this.sellingPlanMatches(formData, item) &&
              this.propertiesMatch(formProperties, this.getComparablePropertiesFromCartItem(item, hasStableSignature))
            );
          });
        } catch (error) {
          return [];
        }
      }

      async updateMatchingCartLines(items, formData) {
        const [targetItem, ...duplicateItems] = items;
        const nextQuantity =
          items.reduce((total, item) => total + item.quantity, 0) + this.getRequestedQuantity(formData);
        const updates = duplicateItems.reduce(
          (result, item) => {
            result[item.key] = 0;
            return result;
          },
          { [targetItem.key]: nextQuantity }
        );
        const body = JSON.stringify({
          updates,
          sections: this.getCartSectionIds(),
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });

        const cart = await response.json();
        this.rememberCartState(cart);

        return cart;
      }

      async submitCartRequest(config, formData) {
        const matchingItems = await this.findMatchingCartLines(formData);

        if (matchingItems.length > 0) {
          return this.updateMatchingCartLines(matchingItems, formData);
        }

        config.body = formData;
        const response = await fetch(`${routes.cart_add_url}`, config);

        const addedItem = await response.json();
        this.prefetchCartState();

        return addedItem;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.appendCustomilyProperties(formData);
        this.setStablePersonalizationSignature(formData);
        this.appendCartSections(formData);

        this.submitCartRequest(config, formData)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.error = false;
            window.location.assign(window.routes?.cart_url || '/cart');
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
